/**
 * Which app window is this?
 *
 * The desktop app used to have exactly one window, so app-wide side effects —
 * native notifications, the dock badge, the offline store and its background
 * sync, the updater — assumed "this webview is the app". Pop-out windows
 * (app-<n>, and messaging-<n> from the first release) are extra webviews
 * running the same bundle, and every page inside a tabbed window is another
 * document in an iframe. Each of them would repeat those side effects: a new
 * message would notify once per window, and two windows would open the same
 * SQLCipher store and sync it concurrently.
 *
 * Those side effects belong to the main window only. A document is "secondary"
 * when its Tauri label is not `main` (an iframe inside a pop-out reports its
 * window's label), when it is inside a frame, or — on the web, or if the label
 * cannot be read — when it was opened at a pop-out URL.
 */
import { isInAnyPopout } from './popout';

export const MAIN_WINDOW_LABEL = 'main';

interface TauriInternals {
  metadata?: { currentWindow?: { label?: string } };
}

/** The Tauri window label, or null in a browser. Synchronous on purpose. */
export function currentDesktopWindowLabel(): string | null {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return null;
  try {
    const internals = (window as unknown as { __TAURI_INTERNALS__?: TauriInternals }).__TAURI_INTERNALS__;
    return internals?.metadata?.currentWindow?.label ?? null;
  } catch {
    return null;
  }
}

/** True for a document inside an iframe (a page in a tabbed window). */
export function isFramedDocument(): boolean {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    return true;
  }
}

export function isSecondaryAppWindow(): boolean {
  const label = currentDesktopWindowLabel();
  if (label !== null && label !== MAIN_WINDOW_LABEL) return true;
  if (isFramedDocument()) return true;
  return isInAnyPopout();
}

/**
 * A pop-out that is its own window or browser tab — the tabbed host, a page
 * pop-out opened on its own, or a desktop app-* / messaging-* window — and not
 * a page framed inside another window. Such a window closes itself on sign-out
 * (TRUS-638); a framed page is torn down by the window that holds it.
 */
export function isTopLevelPopoutWindow(): boolean {
  if (isFramedDocument()) return false;
  const label = currentDesktopWindowLabel();
  if (label !== null && label !== MAIN_WINDOW_LABEL) return true;
  return isInAnyPopout();
}
